import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';

/**
 * Estados de carregamento, vazio e erro.
 *
 * Existem como componentes desde a Fase 1 porque o critério de aceite de **todas** as
 * fases seguintes exige os três em todas as telas — e porque um `EmptyState` que orienta
 * o próximo passo vale mais do que uma tabela em branco.
 */

export function LoadingState({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div
      className="flex items-center justify-center gap-2 p-10 text-sm text-(--color-content-muted)"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-(--color-surface-muted)', className)}
      aria-hidden="true"
    />
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
}

export function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
      <div className="text-(--color-content-muted)" aria-hidden="true">
        {icon ?? <Inbox className="size-8" />}
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        {description !== undefined && (
          <p className="max-w-md text-sm text-(--color-content-muted)">{description}</p>
        )}
      </div>
      {action !== undefined && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  code?: string;
  onRetry?: () => void;
}

/** Mostra mensagem em português e código para diagnóstico — nunca pilha de erro. */
export function ErrorState({
  title = 'Não foi possível continuar',
  message,
  code,
  onRetry,
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-10 text-center" role="alert">
      <AlertTriangle className="size-8 text-(--color-state-danger)" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        <p className="max-w-md text-sm text-(--color-content-muted)">{message}</p>
        {code !== undefined && (
          <p className="font-mono text-xs text-(--color-content-muted)">Código: {code}</p>
        )}
      </div>
      {onRetry !== undefined && (
        <Button variant="secondary" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}
